import { PropsWithChildren, useEffect, useMemo, useState } from "react";
import {
  i18nLanguage,
  i18nNamespace,
  i18nWidgetType,
  loadLanguagePacks,
} from "i18n";
import i18n from "i18n";
import { I18NContext } from "./I18NContext";

type I18NContextProviderProps = PropsWithChildren & {
  defaultNS: i18nNamespace | i18nNamespace[];
  i18nWidgetType: i18nWidgetType;
  i18nLang?: i18nLanguage;
};

/**
 * Widget trees have no Suspense boundary. react-i18next suspends by default
 * until i18n has initialized, and React 18 turns that into minified error #426
 * when the update counts as synchronous input. Firefox exposes `window.event`
 * to host-page callbacks (analytics wrappers included), so the widget's first
 * paint can be treated as a click and the tree unmounts. Opt out before
 * children call `useTranslation`.
 */
function disableI18nSuspense() {
  const reactOptions = (i18n.options.react ??= {});
  reactOptions.useSuspense = false;
}

export const I18NContextProvider = ({
  defaultNS,
  i18nWidgetType,
  i18nLang,
  children,
}: I18NContextProviderProps) => {
  disableI18nSuspense();

  const namespaces = useMemo(
    () => (Array.isArray(defaultNS) ? [...defaultNS] : [defaultNS]),
    [defaultNS],
  );

  // Always start loading so widgets paint a loading UI once. With preloaded i18n (event-submit
  // prod), `getResourceBundle` would otherwise make the initial state false and skip shimmer.
  const [loadingLanguages, setLoadingLanguages] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let timer = 0;

    async function loadLanguages() {
      setLoadingLanguages(true);

      try {
        await loadLanguagePacks(namespaces, i18nWidgetType, i18nLang);
        if (cancelled) return;
        // Defer the language switch off the current task. Firefox keeps
        // `window.event` set for host-page callbacks, and a synchronous
        // language change there makes React throw minified error #426.
        // `changeLanguage` also updates every translated component, so it
        // must not run inside startTransition (that warns once more than
        // ten components update).
        timer = window.setTimeout(() => {
          if (cancelled) return;
          const apply = () => setLoadingLanguages(false);
          if (!i18nLang || i18n.language === i18nLang) {
            apply();
            return;
          }
          void i18n.changeLanguage(i18nLang).catch(error => {
            console.error("Failed to change language:", error);
          }).finally(apply);
        }, 0);
      } catch (error) {
        console.error("Failed to load language packs:", error);
        if (!cancelled) setLoadingLanguages(false);
      }
    }

    loadLanguages();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [namespaces, i18nWidgetType, i18nLang]);

  return (
    <I18NContext.Provider value={{ loading: loadingLanguages }}>
      {children}
    </I18NContext.Provider>
  );
};
